import { create } from 'zustand';
import {
  calculateQueueStats,
  canRetryOperation,
  cleanExpiredOperations,
  createQueuedOperation,
  DEFAULT_QUEUE_CONFIG,
  getPendingOperations,
  type OfflineQueueConfig,
  type QueuedOperation,
  QueuedOperationStatus,
  QueuedOperationType,
  QueuePriority,
  type QueueProcessResult,
  type QueueStats,
  removeCompletedOperations,
} from '@/models/offline-queue.model';
import { useNetworkStatusStore } from '@/stores/network-status.store';
import { useToastStore } from '@/stores/toast.store';

/**
 * @fileoverview Offline queue store (Zustand)
 * @description
 * React/Zustand port of the Angular `OfflineQueueService`. Manages a queue of
 * failed operations for retry when back online. Signal names, method names,
 * persistence (storage key `aiemd_offline_queue` from DEFAULT_QUEUE_CONFIG),
 * and behaviour are reproduced 1:1.
 *
 * Adaptation notes:
 * - The Angular constructor wired loadQueueFromStorage + setupNetworkListener +
 *   startPeriodicCleanup. These are moved into an idempotent `init()` so the
 *   side effects fire once on app mount. `loadQueueFromStorage()` is also called
 *   eagerly at store creation to preserve "queue is populated from storage"
 *   behaviour even before init().
 * - Injected `NetworkStatusService`/`Toast` are replaced by the corresponding
 *   Zustand singletons accessed via `getState()`.
 */

interface OfflineQueueState {
  /** Current queue (reactive) */
  queue: QueuedOperation[];
  /** Queue statistics (reactive) */
  stats: QueueStats;
  /** Whether currently processing queue */
  isProcessing: boolean;
  /** Processing progress (0-100) */
  processingProgress: number;

  addToQueue: (
    type: QueuedOperationType,
    resource: string,
    payload: unknown,
    options?: {
      priority?: QueuePriority;
      description?: string;
      maxAttempts?: number;
      metadata?: Record<string, unknown>;
    },
  ) => Promise<string>;
  removeFromQueue: (operationId: string) => boolean;
  updateOperationStatus: (
    operationId: string,
    status: QueuedOperationStatus,
    error?: string,
  ) => void;
  setProcessor: (fn: (op: QueuedOperation) => Promise<unknown>) => void;
  processQueue: () => Promise<QueueProcessResult[]>;
  cancelOperation: (operationId: string) => boolean;
  cancelAllPending: () => number;
  clearCompleted: () => number;
  clearQueue: () => void;
  updateConfig: (config: Partial<OfflineQueueConfig>) => void;
  getConfig: () => OfflineQueueConfig;
  /** Wire constructor side effects (load + network listener + cleanup). Idempotent. */
  init: () => void;
}

let config: OfflineQueueConfig = { ...DEFAULT_QUEUE_CONFIG };
let processorFn: ((op: QueuedOperation) => Promise<unknown>) | null = null;
let cleanupIntervalId: ReturnType<typeof setInterval> | null = null;
let networkUnsub: (() => void) | null = null;
let initialized = false;

/** Empty stats object (was private emptyStats). */
function emptyStats(): QueueStats {
  return {
    total: 0,
    byStatus: {
      [QueuedOperationStatus.PENDING]: 0,
      [QueuedOperationStatus.PROCESSING]: 0,
      [QueuedOperationStatus.COMPLETED]: 0,
      [QueuedOperationStatus.FAILED]: 0,
      [QueuedOperationStatus.CANCELLED]: 0,
    },
    byType: {
      [QueuedOperationType.CREATE]: 0,
      [QueuedOperationType.UPDATE]: 0,
      [QueuedOperationType.DELETE]: 0,
      [QueuedOperationType.UPLOAD]: 0,
      [QueuedOperationType.RPC]: 0,
      [QueuedOperationType.CUSTOM]: 0,
    },
    byPriority: {
      [QueuePriority.LOW]: 0,
      [QueuePriority.NORMAL]: 0,
      [QueuePriority.HIGH]: 0,
      [QueuePriority.CRITICAL]: 0,
    },
  };
}

export const useOfflineQueueStore = create<OfflineQueueState>((set, get) => {
  const updateStats = (): void => {
    set({ stats: calculateQueueStats(get().queue) });
  };

  const saveQueueToStorage = (): void => {
    try {
      localStorage.setItem(config.storageKey, JSON.stringify(get().queue));
    } catch (error) {
      console.error('Error saving queue to storage:', error);
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        const cleaned = removeCompletedOperations(get().queue);
        set({ queue: cleaned });
        try {
          localStorage.setItem(config.storageKey, JSON.stringify(cleaned));
        } catch {
          console.error('Failed to save queue even after cleanup');
        }
      }
    }
  };

  const loadQueueFromStorage = (): void => {
    try {
      const stored = localStorage.getItem(config.storageKey);
      if (stored) {
        const operations: QueuedOperation[] = JSON.parse(stored);
        const validOperations = cleanExpiredOperations(operations);
        set({ queue: validOperations });
        updateStats();
      }
    } catch (error) {
      console.error('Error loading queue from storage:', error);
    }
  };

  const cleanupQueue = (): void => {
    let currentQueue = get().queue;
    currentQueue = cleanExpiredOperations(currentQueue);
    const oneHourAgo = Date.now() - 60 * 60 * 1000;
    currentQueue = removeCompletedOperations(currentQueue, oneHourAgo);
    set({ queue: currentQueue });
    updateStats();
    saveQueueToStorage();
  };

  const processOperation = async (
    operation: QueuedOperation,
  ): Promise<QueueProcessResult> => {
    if (!processorFn) {
      return {
        operationId: operation.id,
        success: false,
        error: new Error('No processor function'),
        shouldRetry: false,
        attempts: operation.attemptCount,
      };
    }

    get().updateOperationStatus(operation.id, QueuedOperationStatus.PROCESSING);

    try {
      const result = await processorFn(operation);
      get().updateOperationStatus(operation.id, QueuedOperationStatus.COMPLETED);
      return {
        operationId: operation.id,
        success: true,
        data: result,
        shouldRetry: false,
        attempts: operation.attemptCount + 1,
      };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      operation.attemptCount++;
      const shouldRetry = canRetryOperation(operation);
      if (shouldRetry) {
        get().updateOperationStatus(operation.id, QueuedOperationStatus.PENDING, err.message);
      } else {
        get().updateOperationStatus(operation.id, QueuedOperationStatus.FAILED, err.message);
      }
      return {
        operationId: operation.id,
        success: false,
        error: err,
        shouldRetry,
        attempts: operation.attemptCount,
      };
    }
  };

  return {
    queue: [],
    stats: emptyStats(),
    isProcessing: false,
    processingProgress: 0,

    addToQueue: async (type, resource, payload, options) => {
      if (!config.enabled) {
        throw new Error('Offline queue is disabled');
      }

      const currentQueue = get().queue;
      if (currentQueue.length >= config.maxQueueSize) {
        const cleanedQueue = removeCompletedOperations(currentQueue);
        if (cleanedQueue.length >= config.maxQueueSize) {
          throw new Error('Queue is full. Please try again later.');
        }
        set({ queue: cleanedQueue });
      }

      const operation = createQueuedOperation(type, resource, payload, options);

      const updatedQueue = [...get().queue, operation];
      set({ queue: updatedQueue });
      updateStats();
      saveQueueToStorage();

      useToastStore
        .getState()
        .showToast(
          `Operation queued for retry: ${operation.description || operation.resource}`,
          'info',
          3000,
        );

      return operation.id;
    },

    removeFromQueue: (operationId) => {
      const currentQueue = get().queue;
      const index = currentQueue.findIndex((op) => op.id === operationId);
      if (index === -1) return false;
      const updatedQueue = [...currentQueue];
      updatedQueue.splice(index, 1);
      set({ queue: updatedQueue });
      updateStats();
      saveQueueToStorage();
      return true;
    },

    updateOperationStatus: (operationId, status, error) => {
      const currentQueue = get().queue;
      const operation = currentQueue.find((op) => op.id === operationId);
      if (!operation) return;
      operation.status = status;
      if (error) {
        operation.lastError = error;
      }
      operation.lastAttemptAt = Date.now();
      set({ queue: [...currentQueue] });
      updateStats();
      saveQueueToStorage();
    },

    setProcessor: (fn) => {
      processorFn = fn;
    },

    processQueue: async () => {
      if (!processorFn) {
        throw new Error('No processor function set');
      }
      if (get().isProcessing) {
        console.warn('Queue is already being processed');
        return [];
      }

      const operations = getPendingOperations(get().queue);
      if (operations.length === 0) {
        return [];
      }

      set({ isProcessing: true, processingProgress: 0 });

      const results: QueueProcessResult[] = [];
      const maxConcurrent = config.maxConcurrent;

      try {
        for (let i = 0; i < operations.length; i += maxConcurrent) {
          const batch = operations.slice(i, i + maxConcurrent);
          const batchResults = await Promise.all(batch.map((op) => processOperation(op)));
          results.push(...batchResults);
          const progress = Math.round(((i + batch.length) / operations.length) * 100);
          set({ processingProgress: progress });
        }

        const successCount = results.filter((r) => r.success).length;
        const failedCount = results.length - successCount;

        if (successCount > 0) {
          useToastStore
            .getState()
            .showToast(`Processed ${successCount} queued operations`, 'success', 3000);
        }
        if (failedCount > 0) {
          useToastStore
            .getState()
            .showToast(`${failedCount} operations failed and will retry later`, 'warning', 5000);
        }

        return results;
      } finally {
        set({ isProcessing: false, processingProgress: 0 });
      }
    },

    cancelOperation: (operationId) => {
      const currentQueue = get().queue;
      const operation = currentQueue.find((op) => op.id === operationId);
      if (!operation) return false;
      operation.status = QueuedOperationStatus.CANCELLED;
      set({ queue: [...currentQueue] });
      updateStats();
      saveQueueToStorage();
      return true;
    },

    cancelAllPending: () => {
      const currentQueue = get().queue;
      let cancelledCount = 0;
      currentQueue.forEach((op) => {
        if (op.status === QueuedOperationStatus.PENDING) {
          op.status = QueuedOperationStatus.CANCELLED;
          cancelledCount++;
        }
      });
      if (cancelledCount > 0) {
        set({ queue: [...currentQueue] });
        updateStats();
        saveQueueToStorage();
      }
      return cancelledCount;
    },

    clearCompleted: () => {
      const currentQueue = get().queue;
      const remaining = currentQueue.filter(
        (op) => op.status !== QueuedOperationStatus.COMPLETED,
      );
      const removedCount = currentQueue.length - remaining.length;
      if (removedCount > 0) {
        set({ queue: remaining });
        updateStats();
        saveQueueToStorage();
      }
      return removedCount;
    },

    clearQueue: () => {
      set({ queue: [] });
      updateStats();
      saveQueueToStorage();
    },

    updateConfig: (partial) => {
      config = { ...config, ...partial };
    },

    getConfig: () => ({ ...config }),

    init: () => {
      loadQueueFromStorage();
      if (initialized) return;
      initialized = true;

      // setupNetworkListener()
      networkUnsub = useNetworkStatusStore.getState().onStatusChange((event) => {
        if (event.isOnline && !event.wasOnline && config.autoProcessOnReconnect) {
          setTimeout(() => {
            if (processorFn && !get().isProcessing) {
              void get().processQueue();
            }
          }, 2000);
        }
      });

      // startPeriodicCleanup()
      cleanupIntervalId = setInterval(() => {
        cleanupQueue();
      }, 60000 * 5);
    },
  };
});

/** Test/teardown helper: stops timers and clears the network listener. */
export function _resetOfflineQueueInternals(): void {
  if (cleanupIntervalId) {
    clearInterval(cleanupIntervalId);
    cleanupIntervalId = null;
  }
  if (networkUnsub) {
    networkUnsub();
    networkUnsub = null;
  }
  processorFn = null;
  config = { ...DEFAULT_QUEUE_CONFIG };
  initialized = false;
}
